import React, { useState, useEffect } from 'react';
import { supabase } from 'lib/supabase';
import Header from 'components/ui/Header';
import LinkCreationWizard from './components/LinkCreationWizard';
import CampaignTemplates from './components/CampaignTemplates';
import GeneratedLinksPanel from './components/GeneratedLinksPanel';
import BestPracticesPanel from './components/BestPracticesPanel';
import { Helmet } from 'react-helmet';

const LinkGenerator = () => {
  const [_user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [recentLinks, setRecentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Get current user
  const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (authError) {
          console.error('Auth error:', authError);
          return;
        }

        setUser(session?.user || null);

        if (session?.user) {
          // Fetch user's campaigns
          const { data: campaignsData, error: campaignsError } = await supabase
            .from('campaigns')
            .select('id, name, description, status')
            .eq('created_by', session.user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(10);

          if (campaignsError) {
            console.error('Campaigns error:', campaignsError);
          } else {
            setCampaigns(campaignsData || []);
          }

          // Fetch recent tracking links
          const { data: linksData, error: linksError } = await supabase
            .from('tracking_links')
            .select(`
              id, title, description, short_url, target_url, 
              total_clicks, unique_clicks, status, created_at,
              campaigns(name)
            `)
            .eq('created_by', session.user.id)
            .order('created_at', { ascending: false })
            .limit(5);

          if (linksError) {
            console.error('Links error:', linksError);
          } else {
            setRecentLinks(linksData || []);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleLinkCreated = (newLink) => {
  setRecentLinks(prev => [newLink, ...prev.slice(0, 4)]);
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
  };

  if (loading) {
    return (
  <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 text-gray-900 dark:text-neutral-100">
      <Helmet>
        <title>Générateur de Liens - Artisio</title>
        <meta name="description" content="Créez des liens de suivi personnalisés et des QR codes pour promouvoir vos artistes avec un système de tracking complet." />
      </Helmet>
      
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Générateur de Liens</h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-neutral-400">
            Créez des liens de suivi personnalisés et des QR codes pour promouvoir vos artistes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area - Link Creation */}
          <div className="lg:col-span-2 space-y-6">
            <LinkCreationWizard 
              campaigns={campaigns}
              onLinkCreated={handleLinkCreated}
              isCreating={isCreating}
              onCreateNew={handleCreateNew}
            />
            
            <GeneratedLinksPanel 
              recentLinks={recentLinks}
              onCreateNew={handleCreateNew}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <CampaignTemplates 
              campaigns={campaigns}
            />
            
            <BestPracticesPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkGenerator;